import { useState } from 'react';
import { api, ApiError, type ConfigurationResponse } from '../lib/api';

/**
 * Formulário de mudança de um parâmetro de configuração.
 *
 * Por que formulário inline, e não uma caixa do navegador: `prompt()` não é UI.
 * Não valida, não tem botão de cancelar visível, não mostra a faixa permitida e
 * não sobrevive a teclado. Ação que exige motivo é formulário com validação.
 *
 * Três coisas que a tela mostra ANTES de a pessoa tentar:
 *
 *   - a FAIXA permitida, porque recusar depois de digitar é pior que avisar
 *     antes;
 *   - o valor atual e a versão, que é o `expected_revision` enviado. É o que
 *     transforma "outra pessoa mudou enquanto eu lia" em conflito explícito em
 *     vez de sobrescrita silenciosa;
 *   - para onde o valor volta em caso de reversão.
 *
 * E o resultado separa ACEITE de APLICAÇÃO: um comando aceito cuja aplicação
 * falhou não pode aparecer como sucesso (D-047).
 */

type Parametro = ConfigurationResponse['parameters'][number];

/** O comando que cada classe exige. Classe é o eixo de autoridade, não rótulo. */
const COMANDO_POR_CLASSE: Record<string, string> = {
  hard_limit: 'ChangeHardLimit',
  operating_policy: 'ChangeOperatingPolicy',
};

export function classeTemFormulario(classe: string): boolean {
  return classe in COMANDO_POR_CLASSE;
}

/** Texto da faixa, em linguagem de operação. */
export function descreverFaixa(p: Parametro): string | null {
  const f = p.allowed_range;
  if (!f) return null;
  if (f.enum) return `um de: ${f.enum.join(', ')}`;
  if (f.list) return `uma ou mais de: ${f.list.enum.join(', ')}`;
  if (f.pair) return `um par [mínimo, máximo], os dois entre ${f.pair.min} e ${f.pair.max}`;
  if (f.min !== undefined && f.max !== undefined) return `entre ${f.min} e ${f.max}`;
  if (f.min !== undefined) return `a partir de ${f.min}`;
  if (f.max !== undefined) return `até ${f.max}`;
  return null;
}

/**
 * Converte o texto digitado no valor que o comando envia.
 *
 * O SERVIDOR é quem valida; isto aqui só decide a FORMA, e erra para o lado de
 * mandar texto: mandar `"3"` onde o servidor espera `3` recebe uma recusa
 * clara, enquanto adivinhar um número onde o parâmetro é texto gravaria a coisa
 * errada. A forma vem da faixa declarada, não de tentar parsear e ver no que dá.
 */
export function interpretarValor(
  p: Parametro,
  digitado: string,
): { ok: true; valor: unknown } | { ok: false; erro: string } {
  const t = digitado.trim();
  if (t.length === 0) return { ok: false, erro: 'Informe o novo valor.' };

  const f = p.allowed_range;

  if (f?.enum) return { ok: true, valor: t };

  if (f?.list) {
    // Aceita o que as caixas de seleção produzem: itens separados por vírgula.
    // Digitar à mão continua funcionando, e a validação é a mesma, porque o
    // servidor é quem decide e ele valida item por item.
    const itens = t
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
    if (itens.length === 0) return { ok: false, erro: 'Selecione ao menos uma opção.' };
    const fora = itens.filter((x) => !f.list!.enum.includes(x));
    if (fora.length > 0) {
      return { ok: false, erro: `Fora da lista: ${fora.join(', ')}` };
    }
    if (new Set(itens).size !== itens.length) {
      return { ok: false, erro: 'Há item repetido na seleção.' };
    }
    return { ok: true, valor: itens };
  }

  if (f?.pair) {
    const partes = t
      .replace(/^\[|\]$/g, '')
      .split(',')
      .map((x) => x.trim());
    if (partes.length !== 2) {
      return { ok: false, erro: 'Informe dois números separados por vírgula, como 0.25, 0.35' };
    }
    const nums = partes.map(Number);
    if (nums.some((n) => Number.isNaN(n))) {
      return { ok: false, erro: 'Os dois valores do par precisam ser números.' };
    }
    return { ok: true, valor: nums };
  }

  if (f?.min !== undefined || f?.max !== undefined) {
    const n = Number(t);
    if (Number.isNaN(n)) return { ok: false, erro: 'Este parâmetro recebe um número.' };
    return { ok: true, valor: n };
  }

  if (t === 'true' || t === 'false') return { ok: true, valor: t === 'true' };

  // Sem faixa declarada e sem forma de booleano: texto. É o caso de documento
  // de policy e de fuso horário.
  return { ok: true, valor: t };
}

type Resultado =
  | { kind: 'aplicado'; resumo: string }
  | { kind: 'aceito_pendente'; resumo: string }
  | { kind: 'falhou'; resumo: string };

export function FormularioDeMudanca({
  parametro,
  onAplicado,
}: {
  parametro: Parametro;
  /** Recarrega a projeção. Salvar sem reler deixa a tela mostrando o valor velho. */
  onAplicado: () => void;
}) {
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);

  const comando = COMANDO_POR_CLASSE[parametro.class];
  const faixa = descreverFaixa(parametro);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setErro(null);
    setResultado(null);

    if (motivo.trim().length < 5) {
      setErro('O motivo é obrigatório e fica no registro de auditoria.');
      return;
    }
    const interpretado = interpretarValor(parametro, valor);
    if (!interpretado.ok) {
      setErro(interpretado.erro);
      return;
    }

    setEnviando(true);
    try {
      const r = await api.sendCommand({
        command_type: comando!,
        target_ref: { object_type: 'Configuration', object_id: parametro.parameter_key },
        reason: motivo.trim(),
        payload: { parameter_key: parametro.parameter_key, new_value: interpretado.valor },
        // A versão que ESTA tela leu. Sem isto, duas pessoas editando o mesmo
        // parâmetro produzem a última escrita ganhando em silêncio.
        expected_revision: parametro.version ?? 0,
      });

      if (r.execution === null) {
        setResultado({
          kind: 'aceito_pendente',
          resumo: r.message ?? 'Comando aceito. A aplicação está pendente.',
        });
      } else if (r.execution.state === 'applied') {
        setResultado({ kind: 'aplicado', resumo: r.execution.summary });
        setValor('');
        setMotivo('');
        onAplicado();
      } else if (r.execution.state === 'partially_applied') {
        setResultado({ kind: 'aceito_pendente', resumo: r.execution.summary });
        onAplicado();
      } else {
        setResultado({ kind: 'falhou', resumo: r.execution.summary });
      }
    } catch (e) {
      // A mensagem vem do servidor, que é quem sabe o motivo. Um texto genérico
      // aqui apagaria "5000 está acima do máximo permitido (60)".
      setErro(e instanceof ApiError ? e.message : 'Não foi possível enviar o comando.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="mt-3 space-y-2 border-t border-line pt-3">
      <h3 className="text-sm font-semibold">Alterar valor</h3>

      <p className="text-xs text-ink-muted">
        Versão atual: <strong>{parametro.version ?? 'nenhuma'}</strong>
        {faixa ? (
          <>
            {' · '}Aceita: <strong>{faixa}</strong>
          </>
        ) : null}
      </p>

      {parametro.allowed_range?.list ? (
        <fieldset>
          <legend className="block text-xs text-ink-muted">Novo valor</legend>
          {/*
            Caixas de seleção, e não campo de texto. Pedir a quem opera que
            digite a lista sem errar a vírgula transformaria a recusa do
            servidor em erro de digitação, e a forma da faixa é declarada, então
            a tela pode oferecer as opções.
          */}
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {parametro.allowed_range.list.enum.map((opcao) => {
              const marcadas = valor
                .split(',')
                .map((x) => x.trim())
                .filter((x) => x.length > 0);
              return (
                <label key={opcao} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    name="novo-valor"
                    value={opcao}
                    checked={marcadas.includes(opcao)}
                    onChange={(e) => {
                      const proximas = e.target.checked
                        ? [...marcadas, opcao]
                        : marcadas.filter((x) => x !== opcao);
                      // Preserva a ORDEM declarada da faixa, e não a ordem dos
                      // cliques: a ordem das ações é lida por quem revisa, e
                      // duas seleções iguais em ordens diferentes seriam duas
                      // versões de configuração para o mesmo valor.
                      setValor(
                        parametro
                          .allowed_range!.list!.enum.filter((o) => proximas.includes(o))
                          .join(', '),
                      );
                    }}
                  />
                  {opcao}
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : (
        <div>
          <label htmlFor="novo-valor" className="block text-xs text-ink-muted">
            Novo valor
          </label>
          <input
            id="novo-valor"
            name="novo-valor"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoComplete="off"
            className="mt-1 w-full rounded border border-line px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      )}

      <div>
        <label htmlFor="motivo-mudanca" className="block text-xs text-ink-muted">
          Motivo (obrigatório, fica na auditoria)
        </label>
        <textarea
          id="motivo-mudanca"
          name="motivo-mudanca"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded border border-line px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>

      {erro ? (
        <p role="alert" className="text-sm text-danger-ink">
          {erro}
        </p>
      ) : null}

      {resultado ? (
        <p
          role="status"
          className={
            resultado.kind === 'aplicado'
              ? 'text-sm text-ink'
              : resultado.kind === 'falhou'
                ? 'text-sm text-danger-ink'
                : 'text-sm text-ink-muted'
          }
        >
          {resultado.kind === 'aplicado'
            ? 'Aplicado. '
            : resultado.kind === 'falhou'
              ? 'Não aplicado. '
              : 'Aceito, aplicação pendente. '}
          {resultado.resumo}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={enviando}
          className="rounded border border-line-strong px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
        >
          {enviando ? 'Enviando...' : 'Enviar mudança'}
        </button>
        <button
          type="button"
          onClick={() => {
            setValor('');
            setMotivo('');
            setErro(null);
            setResultado(null);
          }}
          className="rounded border border-line px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
        >
          Limpar
        </button>
      </div>
    </form>
  );
}
